import { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Clock, FileText, XCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import timeInLieuService, { type TilAgreement } from '../../services/timeInLieu';
import { formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';

/**
 * Overtime or time in lieu, for one employee, on the staff profile (HR seat).
 *
 * The employee asks and accepts the agreement; HR approves it for the
 * employer, or declines it. Either side can end it. Overtime is paid unless an
 * agreement is in force on the day (HHNodeServer timeInLieuAgreementService).
 */
export default function TilAgreementCard({ userId, onChanged }: {
  userId: number;
  /** Something was approved, declined or ended: the page's pending list may have moved. */
  onChanged?: () => void;
}) {
  const orgTz = useOrgTimezone();
  const [rows, setRows] = useState<TilAgreement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showText, setShowText] = useState(false);
  const [ending, setEnding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await timeInLieuService.list(userId);
      if (res.success && res.data) setRows(res.data);
      else setError('Could not load this person\'s time in lieu agreement.');
    } catch (err) {
      setError((err as { message?: string })?.message || 'Could not load this person\'s time in lieu agreement.');
    }
  }, [userId]);

  useEffect(() => {
    setRows(null);
    setNote('');
    setShowText(false);
    setEnding(false);
    load();
  }, [load]);

  const act = async (fn: () => Promise<{ success: boolean; error?: { message?: string } }>) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (!res.success) {
        setError(res.error?.message || 'That did not work. Try again.');
        return;
      }
      setNote('');
      setEnding(false);
      await load();
      onChanged?.();
    } catch (err) {
      const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setError(e?.response?.data?.error?.message || e?.message || 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const pending = rows?.find(r => r.status === 'pending') ?? null;
  const approved = rows?.find(r => r.status === 'approved') ?? null;
  const last = rows?.find(r => r !== pending && r !== approved) ?? null;
  const shown = pending ?? approved;

  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-neutral-900">Overtime or time in lieu</h3>
            {rows === null && !error && <p className="text-sm text-neutral-500">Loading…</p>}
            {rows !== null && !pending && !approved && (
              <p className="text-sm text-neutral-600">
                <strong>Paid overtime.</strong> They have no time in lieu agreement, so overtime is paid.
                They can ask for time in lieu from their own timesheet.
                {last?.status === 'ended' && last.ended_on && ` Their last agreement ended on ${formatLocalDateInTz(last.ended_on, orgTz)}.`}
                {last?.status === 'declined' && ' Their last request was declined.'}
              </p>
            )}
            {approved && (
              <p className="text-sm text-neutral-600">
                <strong className="text-green-700">Time in lieu</strong> since{' '}
                {approved.effective_from ? formatLocalDateInTz(approved.effective_from, orgTz) : '—'}
                {approved.decided_by_name ? `, approved by ${approved.decided_by_name}` : ''}.
                Their overtime is banked hour for hour instead of paid.
              </p>
            )}
            {pending && (
              <p className="text-sm text-amber-800">
                <strong>Asked for time in lieu</strong> on {formatDateTimeInTz(pending.requested_at, orgTz)} and
                accepted the agreement. It needs your approval before it applies; until then their overtime is paid.
              </p>
            )}
          </div>
        </div>

        {shown && (
          <div>
            <button
              type="button"
              onClick={() => setShowText(v => !v)}
              className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline"
            >
              <FileText className="h-4 w-4" />
              {showText ? 'Hide' : 'Read'} the agreement they accepted (version {shown.agreement_version})
            </button>
            {showText && (
              <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700 whitespace-pre-wrap break-words">
                {shown.agreement_text}
                <p className="mt-2 text-xs text-neutral-500 break-all">Fingerprint {shown.agreement_hash}</p>
              </div>
            )}
          </div>
        )}

        {pending && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" htmlFor="til-decision-note">
              Note to them (optional)
            </label>
            <textarea
              id="til-decision-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="e.g. Approved: take it at a time we agree, within six months"
            />
            <p className="text-xs text-neutral-500">
              Approving records that the employer agrees, from today. Check it is allowed under their award or agreement first.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" disabled={busy} onClick={() => act(() => timeInLieuService.decide(pending.id, true, note))}>
                <CheckCircle className="h-4 w-4 mr-1" /> Approve time in lieu
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => timeInLieuService.decide(pending.id, false, note))}>
                <XCircle className="h-4 w-4 mr-1" /> Decline
              </Button>
            </div>
          </div>
        )}

        {approved && (ending ? (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700" htmlFor="til-end-reason">
              Why is it ending? (optional, they will see this)
            </label>
            <textarea
              id="til-end-reason"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
            <p className="text-xs text-neutral-500">
              Overtime from today is paid. Time in lieu they have banked stays in their balance.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => timeInLieuService.end(approved.id, note))}>
                End the agreement
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setEnding(false); setNote(''); }}>
                Keep it
              </Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEnding(true)}>End the agreement</Button>
        ))}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
